
import React, { useMemo, useEffect } from 'react';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useSearchParams } from 'hooks/urlHooks';
import { useDispatch, useSelector } from 'react-redux';
import { TRootState } from 'redux/reducers/rootReducer';
import useLookupDynamic from 'hooks/useLookupDynamic';
import ELookupAPIKeys from 'types/backend_models/lookup_status/ELookupAPIKeys';
import { ResetScroll } from 'functions/General';
import useCombinedRequestStatus from 'hooks/useCombinedRequestStatus';
import { setFetchDeliveryError, resetDeliveryFetchStatus } from 'redux/reducers/data_action_reducers/deliveries/DeliveryDataReducer';
import { fetchSingleDelivery } from 'redux/actions/dataActions/deliveries/DeliveryDataActions';
import { fetchDeliveryLinesByDeliveryId } from 'redux/actions/dataActions/deliveries/DeliveryLinesByDeliveryIdActions';
import { fetchDeliveryDocuments } from 'redux/actions/dataActions/deliveries/DeliveryDocumentsDataActions';
import RequestStatus from 'types/general/requests/RequestStatus';
import LayoutRow from 'components/content/shared/styling/LayoutRow';
import Divider from 'components/content/shared/styling/Divider';
import ElevatedContainer from 'components/content/shared/styling/ElevatedContainer';
import { LargeLoadingIndicator } from 'components/content/shared/other/LoadingIndicator';
import { LargeErrorMessage } from 'components/content/shared/other/ErrorMessage';
import BreadCrumbs from 'components/navigation/breadcrumb/BreadCrumbs';
import { allRoutes } from 'components/navigation/routes';
import { RouteId } from 'types/frontend_models/navigation/RouteId';
import { DeliveryDetailInfoOne } from './DeliveryDetailInfo';
import { DeliveryDetailRelatedDocumentsContainer } from './DeliveryDetailRelatedDocumentsContainer';
import { IDocument } from 'types/backend_models/documents/IDocument';
import { DeliveryDetailLinesContainer } from './DeliveryDetailLinesContainer';
import { IDeliveryLine } from 'types/backend_models/deliveries/IDeliveryLine';
import ILookupModel from 'types/backend_models/lookup_status/ILookupModel';
import { DeliveryDetailLinks } from './DeliveryDetailLinks';
import { IDelivery } from 'types/backend_models/deliveries/IDelivery';

var setupCompleted = false;

function DeliveryDetailPage()
{
	const thunkDispatch = useThunkDispatch();
	const { searchParams } = useSearchParams();
	const dispatch = useDispatch();

	const deliveryId = useMemo(() =>
	{
		// console.log('searchParams', searchParams);
		// Find the orderId if it was passed
		const param = searchParams.find(param => param.key === 'id');
		return param ? Number(param.value) : -1;
	}, [searchParams]);

	const deliveryState = useSelector((state: TRootState) => state.data.deliveries.detail);
	const deliveryLinesState = useSelector((state: TRootState) => state.data.deliveries.detail_lines);
	const deliveryDocumentsState = useSelector((state: TRootState) => state.data.deliveries.detail_documents);
	const unitsOfMeasureState = useLookupDynamic(ELookupAPIKeys.unitOfMeasure);

	useEffect(() =>
	{
		// Reset window scroll
		ResetScroll();
		// Get data
		if (deliveryId !== -1) {
			thunkDispatch(fetchSingleDelivery(deliveryId));
			thunkDispatch(fetchDeliveryLinesByDeliveryId(deliveryId));
			thunkDispatch(fetchDeliveryDocuments(deliveryId));
		} else {
			dispatch(setFetchDeliveryError({ error: `${DeliveryDetailPage.name}: No delivery id in search url` }));
		}

		return function cleanup()
		{
			dispatch(resetDeliveryFetchStatus());
			setupCompleted = false;
		};

		// eslint-disable-next-line
	}, []);

	const combinedRequestStatus = useCombinedRequestStatus(
		deliveryState,
		deliveryLinesState,
		deliveryDocumentsState,
		unitsOfMeasureState
	);

	if (combinedRequestStatus.requestStatus === RequestStatus.pending) {
		setupCompleted = true;
		console.log('delivery item', deliveryState.data);
	}

	if (deliveryId === -1) {
		setupCompleted = true;
	}



	return (
		<div className="mh-vh-80 pos-rel">
			{
				combinedRequestStatus.requestStatus === RequestStatus.succes &&
				<div>
					<LayoutRow style={{ alignItems: 'center' }} className="mgb-20">
						<h1 className="mg-0 col-12 col-m-6">Delivery Details</h1>
						<div className="col-auto txt-m-right">
							<BreadCrumbs data={
								[
									{ label: 'Deliveries overview', path: allRoutes.find(r => r.routeId === RouteId.deliveriesOverview)?.path },
									{ label: 'Delivery details' }
								]
							} />
						</div>
					</LayoutRow>

					<ElevatedContainer>
						<LayoutRow>
							<div className="col-12 col-l-5 col-xl-4">
								<DeliveryDetailInfoOne delivery={deliveryState.data} />
							</div>
							<Divider breakPoint='l' className="bg-border-color-light" />
							<div className="col-12 col-l-6 col-xl-7">
								<DeliveryDetailRelatedDocumentsContainer
									documents={deliveryDocumentsState.data.results as IDocument[]}
								/>
							</div>
						</LayoutRow>
						<LayoutRow>
							<Divider className="bg-border-color-light" />
							<div className="col-12">
								<DeliveryDetailLinesContainer
									deliveryLines={deliveryLinesState.data as IDeliveryLine[]}
									unitsOfMeasurement={unitsOfMeasureState.data as ILookupModel[]}
								/>
							</div>
						</LayoutRow>
						<LayoutRow>
							<Divider className="bg-border-color-light" />
							<div className="col-12">
								<DeliveryDetailLinks delivery={deliveryState.data as IDelivery} />
							</div>
						</LayoutRow>
					</ElevatedContainer>
				</div>
			}
			{(!setupCompleted || combinedRequestStatus.requestStatus === RequestStatus.pending ||
				deliveryState.requestStatus === RequestStatus.idle) &&
				<LargeLoadingIndicator />
			}
			{combinedRequestStatus.requestStatus === RequestStatus.error &&
				<LargeErrorMessage message="Could not find the delivery you're looking for." />
			}
		</div>
	);
};

export default DeliveryDetailPage;